import { ThemeProvider, createTheme } from "@mui/material/styles";
import "@mui/material/styles";
import React, {
  PropsWithChildren,
  createContext,
  useEffect,
  useState,
} from "react";
import { COLORS } from "../../constant/color";

type ColorModeContextType = {
  mode: string;
  setMode: () => void;
};

// Add custom colors to palette
declare module "@mui/material/styles" {
  interface Palette {
    container: {
      main: string;
    };
    splishSplash: {
      main: string;
    };
    alienated: {
      main: string;
    };
    cerebralGrey: {
      main: string;
    };
  }
  interface PaletteOptions {
    container?: {
      main: string;
    };
    splishSplash?: {
      main: string;
    };
    alienated?: {
      main: string;
    };
    cerebralGrey?: {
      main: string;
    };
  }
}

// Create the context
export const ColorModeContext = createContext<ColorModeContextType>(
  {} as ColorModeContextType
);

// Light Theme (with all static colors)
const customLightTheme = createTheme({
  palette: {
    mode: "light",
    background: {
      default: COLORS.light.backgroundDefault,
      paper: COLORS.light.backgroundPaper,
    },
    primary: {
      main: COLORS.light.primaryMain,
      contrastText: COLORS.light.primaryContrastText,
    },
    text: {
      primary: COLORS.light.textPrimary,
      secondary: COLORS.light.textSecondary,
    },
    container: {
      main: COLORS.light.containerMain,
    },
    splishSplash: {
      main: COLORS.light.splishSplash,
    },
    alienated: {
      main: COLORS.light.alienated,
    },
    cerebralGrey: {
      main: COLORS.light.cerebralGrey,
    },
    error: {
      main: COLORS.light.errorMain,
      dark: COLORS.light.errorDark,
    },
    divider: COLORS.light.divider,
    grey: {
      400: COLORS.light.grey400,
      500: COLORS.light.grey500,
      600: COLORS.light.grey600,
    },
    success: {
      main: COLORS.light.successMain,
      dark: COLORS.light.successDark,
    },
    warning: {
      main: COLORS.light.warningMain,
    },
    info: {
      main: COLORS.light.infoMain,
    },
    action: {
      hover: COLORS.light.actionHover,
    },
    common: {
      white: COLORS.light.white,
      black: COLORS.light.black,
    },
    
  },
});

// Dark Theme (with all static colors)
const customDarkTheme = createTheme({
  palette: {
    mode: "dark",
    background: {
      default: COLORS.dark.backgroundDefault,
      paper: COLORS.dark.backgroundPaper,
    },
    primary: {
      main: COLORS.dark.primaryMain,
      contrastText: COLORS.dark.primaryContrastText,
    },
    text: {
      primary: COLORS.dark.textPrimary,
      secondary: COLORS.dark.textSecondary,
    },
    container: {
      main: COLORS.dark.containerMain,
    },
    splishSplash: {
      main: COLORS.dark.splishSplash,
    },
    alienated: {
      main: COLORS.dark.alienated,
    },
    cerebralGrey: {
      main: COLORS.dark.cerebralGrey,
    },
    error: {
      main: COLORS.dark.errorMain,
      dark: COLORS.dark.errorDark,
    },
    divider: COLORS.dark.divider,
    grey: {
      400: COLORS.dark.grey400,
      500: COLORS.dark.grey500,
      600: COLORS.dark.grey600,
    },
    success: {
      main: COLORS.dark.successMain,
      dark: COLORS.dark.successDark,
    },
    warning: {
      main: COLORS.dark.warningMain,
    },
    info: {
      main: COLORS.dark.infoMain,
    },
    action: {
      hover: COLORS.dark.actionHover,
    },
    common: {
      white: COLORS.dark.white,
      black: COLORS.dark.black,
      
    },
    
  },
});

// Provider with toggle + localStorage
export const ColorModeContextProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const colorModeFromLocalStorage = localStorage.getItem("colorMode");
  const isSystemPreferenceDark = window?.matchMedia(
    "(prefers-color-scheme: dark)"
  ).matches;

  const systemPreference = isSystemPreferenceDark ? "dark" : "light";
  const [mode, setMode] = useState(() => colorModeFromLocalStorage || "dark");

  useEffect(() => {
    window.localStorage.setItem("colorMode", mode);
  }, [mode]);

  const setColorMode = () => {
    setMode((prev) => (prev === "light" ? "dark" : "light"));
  };

  return (
    <ColorModeContext.Provider
      value={{
        setMode: setColorMode,
        mode,
      }}
    >
      <ThemeProvider
        theme={mode === "light" ? customLightTheme : customDarkTheme}
      >
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};
